// client/src/components/InspirationBoardPanel.tsx
// Shared inspiration board panel — email-keyed shared board of booking references,
// links, and uploads. Used in InspirationStudio (portal), AdminAction tab, and
// ManageContacts drawer.
//
// Props:
//   imageUrlFn(id)  — builds the URL for /image proxy endpoint
//   apiBase         — base path for board API (/api/portal/inspiration/board or /api/admin/inspiration/:email)
//   headers         — auth headers (Authorization: Bearer <token> or x-admin-key)
//   readOnly        — if true, hides delete / add / move controls (default false)
import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface InspirationItemShape {
  id: number;
  clientEmail: string | null;
  sourceType: string | null;
  storagePath: string;
  originalUrl: string | null;
  previewUrl: string | null;
  title: string | null;
  caption: string | null;
  folderId: number | null;
  bookingId: number | null;
  createdAt: string;
}

export interface InspirationFolderShape {
  id: number;
  clientEmail: string;
  name: string;
  createdAt: string;
}

interface Props {
  imageUrlFn: (id: number) => string;
  apiBase: string;
  /**
   * Override URL for the board GET (items + folders). If omitted, falls back
   * to apiBase. Useful when admin boards have a /items suffix.
   */
  boardEndpoint?: string;
  headers: Record<string, string>;
  readOnly?: boolean;
  queryKey: string[];
}

async function apiFetch(url: string, opts: RequestInit = {}) {
  const res = await fetch(url, opts);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any).error || `HTTP ${res.status}`);
  }
  return res.json();
}

/** Only render http/https URLs — blocks javascript: and other unsafe schemes */
function safeHref(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const p = new URL(url);
    return (p.protocol === "http:" || p.protocol === "https:") ? url : null;
  } catch {
    return null;
  }
}

const SOURCE_BADGE: Record<string, { label: string; color: string }> = {
  booking_reference: { label: "Ref", color: "#8b5cf6" },
  link: { label: "Link", color: "#0ea5e9" },
  upload: { label: "Upload", color: "#14b8a6" },
  ai_generated: { label: "AI", color: "#f59e0b" },
};

export default function InspirationBoardPanel({ imageUrlFn, apiBase, boardEndpoint, headers, readOnly = false, queryKey }: Props) {
  const qc = useQueryClient();
  const [selectedFolder, setSelectedFolder] = useState<number | null | "all">("all");
  const [lightbox, setLightbox] = useState<InspirationItemShape | null>(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [addingLink, setAddingLink] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [showFolderCreate, setShowFolderCreate] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useQuery<{ items: InspirationItemShape[]; folders: InspirationFolderShape[] }>({
    queryKey,
    queryFn: () => apiFetch(boardEndpoint ?? apiBase, { headers }),
    staleTime: 30_000,
  });

  const items = data?.items ?? [];
  const folders = data?.folders ?? [];

  const filteredItems = selectedFolder === "all"
    ? items
    : items.filter(i => i.folderId === selectedFolder);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`${apiBase}/${id}`, { method: "DELETE", headers }),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  const moveMutation = useMutation({
    mutationFn: ({ id, folderId }: { id: number; folderId: number | null }) =>
      apiFetch(`${apiBase}/${id}/move`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ folderId }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  const createFolderMutation = useMutation({
    mutationFn: (name: string) => apiFetch(`${apiBase}/folders`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); setNewFolderName(""); setShowFolderCreate(false); },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`${apiBase}/folders/${id}`, { method: "DELETE", headers }),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); setSelectedFolder("all"); },
  });

  const addLinkMutation = useMutation({
    mutationFn: (url: string) => apiFetch(`${apiBase}/link`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ url, folderId: selectedFolder === "all" ? null : selectedFolder }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); setLinkUrl(""); setAddingLink(false); },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("image", file);
      if (selectedFolder !== "all" && selectedFolder !== null) {
        fd.append("folderId", String(selectedFolder));
      }
      const res = await fetch(`${apiBase}/upload`, { method: "POST", headers, body: fd });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error || `HTTP ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey }); setUploadError(null); },
    onError: (e: Error) => setUploadError(e.message),
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadMutation.mutate(file);
    e.target.value = "";
  }

  if (isLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 0", color: "#666" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>🎨</div>
          <p style={{ margin: 0, fontSize: 13 }}>Loading inspiration board…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "20px 0", color: "#ef4444", fontSize: 13 }}>
        Failed to load inspiration board.
      </div>
    );
  }

  return (
    <div data-testid="inspiration-board-panel">
      {/* Folder sidebar */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16, alignItems: "center" }}>
        <button
          data-testid="folder-all"
          onClick={() => setSelectedFolder("all")}
          style={{
            padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
            background: selectedFolder === "all" ? "#14b8a6" : "#111",
            color: selectedFolder === "all" ? "#000" : "#888",
            border: selectedFolder === "all" ? "none" : "1px solid #222",
          }}
        >
          All ({items.length})
        </button>
        {folders.map(f => (
          <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button
              data-testid={`folder-${f.id}`}
              onClick={() => setSelectedFolder(f.id)}
              style={{
                padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
                background: selectedFolder === f.id ? "#8b5cf6" : "#111",
                color: selectedFolder === f.id ? "#fff" : "#888",
                border: selectedFolder === f.id ? "none" : "1px solid #222",
              }}
            >
              📁 {f.name} ({items.filter(i => i.folderId === f.id).length})
            </button>
            {!readOnly && (
              <button
                data-testid={`delete-folder-${f.id}`}
                onClick={() => { if (confirm(`Delete folder "${f.name}"?`)) deleteFolderMutation.mutate(f.id); }}
                title="Delete folder"
                style={{ background: "none", border: "none", cursor: "pointer", color: "#444", fontSize: 13, padding: "2px 4px" }}
              >✕</button>
            )}
          </div>
        ))}
        {!readOnly && (
          showFolderCreate ? (
            <form
              onSubmit={e => { e.preventDefault(); if (newFolderName.trim()) createFolderMutation.mutate(newFolderName.trim()); }}
              style={{ display: "flex", gap: 6, alignItems: "center" }}
            >
              <input
                data-testid="input-new-folder"
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                placeholder="Folder name"
                autoFocus
                style={{ background: "#111", border: "1px solid #333", color: "#fff", borderRadius: 5, padding: "4px 8px", fontSize: 12, width: 120 }}
              />
              <button type="submit" data-testid="btn-create-folder" style={{ padding: "4px 10px", borderRadius: 5, background: "#14b8a6", color: "#000", border: "none", fontSize: 12, cursor: "pointer" }}>
                {createFolderMutation.isPending ? "…" : "Add"}
              </button>
              <button type="button" onClick={() => setShowFolderCreate(false)} style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 13 }}>✕</button>
            </form>
          ) : (
            <button
              data-testid="btn-show-folder-create"
              onClick={() => setShowFolderCreate(true)}
              style={{ padding: "5px 10px", borderRadius: 6, fontSize: 11, background: "none", border: "1px dashed #333", color: "#555", cursor: "pointer" }}
            >+ Folder</button>
          )
        )}
      </div>

      {/* Action row: Save Link + Upload Image */}
      {!readOnly && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
          {addingLink ? (
            <form
              onSubmit={e => { e.preventDefault(); if (linkUrl.trim()) addLinkMutation.mutate(linkUrl.trim()); }}
              style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", flex: "1 1 300px" }}
            >
              <input
                data-testid="input-link-url"
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                placeholder="Paste Pinterest, Instagram, or any URL…"
                autoFocus
                style={{ flex: "1 1 200px", background: "#111", border: "1px solid #333", color: "#fff", borderRadius: 6, padding: "7px 10px", fontSize: 13 }}
              />
              <button
                type="submit"
                data-testid="btn-add-link"
                disabled={addLinkMutation.isPending}
                style={{ padding: "7px 14px", borderRadius: 6, background: "#0ea5e9", color: "#000", border: "none", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
              >{addLinkMutation.isPending ? "Saving…" : "Save Link"}</button>
              <button type="button" onClick={() => setAddingLink(false)} style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 13 }}>Cancel</button>
            </form>
          ) : (
            <button
              data-testid="btn-show-add-link"
              onClick={() => setAddingLink(true)}
              style={{ padding: "7px 14px", borderRadius: 6, background: "#0c1a24", border: "1px solid #0ea5e940", color: "#38bdf8", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
            >🔗 Save Link</button>
          )}

          {/* Image upload */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            data-testid="input-upload-image"
            onChange={handleFileChange}
          />
          <button
            data-testid="btn-upload-image"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            style={{ padding: "7px 14px", borderRadius: 6, background: "#0c180e", border: "1px solid #14b8a640", color: "#4ade80", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
          >
            {uploadMutation.isPending ? "Uploading…" : "⬆ Upload Image"}
          </button>
          {uploadError && (
            <span style={{ fontSize: 12, color: "#ef4444", alignSelf: "center" }}>{uploadError}</span>
          )}
        </div>
      )}

      {/* Grid */}
      {filteredItems.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#444" }}>
          <p style={{ fontSize: 28, margin: "0 0 10px" }}>🖼️</p>
          <p style={{ margin: 0, fontSize: 13, color: "#555" }}>
            {items.length === 0
              ? "No inspiration images yet. They appear here automatically from your booking reference images."
              : "No items in this folder."}
          </p>
        </div>
      ) : (
        <div
          data-testid="inspiration-board-grid"
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 10 }}
        >
          {filteredItems.map(item => {
            const badge = SOURCE_BADGE[item.sourceType ?? ""] ?? SOURCE_BADGE.upload;
            const safePrev = safeHref(item.previewUrl);
            const imgSrc = item.sourceType === "link" && safePrev
              ? safePrev
              : imageUrlFn(item.id);

            return (
              <div key={item.id} data-testid={`inspiration-item-${item.id}`}>
                <div
                  style={{ position: "relative", borderRadius: 8, overflow: "hidden", background: "#111", border: "1px solid #1a1a1a", cursor: "pointer", aspectRatio: "1" }}
                  onClick={() => setLightbox(item)}
                >
                  <img
                    src={imgSrc}
                    alt={item.title ?? "Inspiration"}
                    loading="lazy"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
                  />
                  <span style={{
                    position: "absolute", top: 4, left: 4, fontSize: 9, fontWeight: 700,
                    padding: "2px 5px", borderRadius: 3, background: `${badge.color}cc`, color: "#fff",
                  }}>
                    {badge.label}
                  </span>
                  {!readOnly && (
                    <button
                      data-testid={`delete-item-${item.id}`}
                      onClick={e => { e.stopPropagation(); if (confirm("Remove from board?")) deleteMutation.mutate(item.id); }}
                      style={{
                        position: "absolute", top: 4, right: 4, width: 20, height: 20,
                        borderRadius: "50%", background: "rgba(0,0,0,0.6)", border: "none",
                        color: "#fff", fontSize: 12, cursor: "pointer", lineHeight: "20px", padding: 0, textAlign: "center",
                      }}
                    >✕</button>
                  )}
                  {item.caption && (
                    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "4px 6px", background: "rgba(0,0,0,0.65)", fontSize: 10, color: "#ddd", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.caption}
                    </div>
                  )}
                </div>
                {/* Move-to-folder control */}
                {!readOnly && folders.length > 0 && (
                  <select
                    data-testid={`move-item-${item.id}`}
                    value={item.folderId ?? ""}
                    onChange={e => {
                      const val = e.target.value;
                      moveMutation.mutate({ id: item.id, folderId: val === "" ? null : Number(val) });
                    }}
                    onClick={e => e.stopPropagation()}
                    style={{
                      width: "100%", marginTop: 4, background: "#111", border: "1px solid #222",
                      color: "#555", borderRadius: 4, fontSize: 10, padding: "2px 4px", cursor: "pointer",
                    }}
                  >
                    <option value="">No folder</option>
                    {folders.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          data-testid="inspiration-lightbox"
          onClick={() => setLightbox(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        >
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: "90vh", borderRadius: 12, overflow: "hidden", background: "#0a0a0a", boxShadow: "0 0 60px rgba(0,0,0,0.8)" }}>
            {(() => {
              const safePrev = safeHref(lightbox.previewUrl);
              const src = lightbox.sourceType === "link" && safePrev ? safePrev : imageUrlFn(lightbox.id);
              return (
                <img
                  src={src}
                  alt={lightbox.title ?? "Inspiration"}
                  style={{ display: "block", maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }}
                />
              );
            })()}
            <div style={{ padding: "12px 16px", borderTop: "1px solid #1a1a1a" }}>
              {lightbox.title && <p style={{ margin: "0 0 4px", fontWeight: 600, fontSize: 14, color: "#fff" }}>{lightbox.title}</p>}
              {lightbox.caption && <p style={{ margin: "0 0 4px", fontSize: 12, color: "#888" }}>{lightbox.caption}</p>}
              {safeHref(lightbox.originalUrl) && (
                <a href={safeHref(lightbox.originalUrl)!} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "#38bdf8" }}>
                  View original →
                </a>
              )}
              {/* Move to folder from lightbox */}
              {!readOnly && folders.length > 0 && (
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, color: "#555" }}>Folder:</span>
                  <select
                    data-testid="lightbox-move-folder"
                    value={lightbox.folderId ?? ""}
                    onChange={e => {
                      const val = e.target.value;
                      const newFolderId = val === "" ? null : Number(val);
                      moveMutation.mutate({ id: lightbox.id, folderId: newFolderId });
                      setLightbox({ ...lightbox, folderId: newFolderId });
                    }}
                    style={{ background: "#111", border: "1px solid #333", color: "#aaa", borderRadius: 4, fontSize: 11, padding: "3px 6px" }}
                  >
                    <option value="">No folder</option>
                    {folders.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                <button onClick={() => setLightbox(null)} style={{ padding: "6px 14px", borderRadius: 6, background: "#111", border: "1px solid #333", color: "#aaa", fontSize: 12, cursor: "pointer" }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
